import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { eur } from "@/components/LineItemsEditor";
import { BarChart3, Loader2, Users, Car, Receipt, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

export default function Stats() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const cards = [
    { label: "CA cette année", value: eur(data.ca_year), icon: TrendingUp },
    { label: "CA total encaissé", value: eur(data.ca_total), icon: TrendingUp },
    { label: "Panier moyen", value: eur(data.panier_moyen), icon: Receipt },
    { label: "Résultat estimé", value: eur(data.resultat_estime), icon: TrendingUp },
    { label: "Prestations (RDV)", value: data.nb_prestations, icon: BarChart3 },
    { label: "Factures", value: data.nb_invoices, icon: Receipt },
    { label: "Clients", value: data.nb_clients, icon: Users },
    { label: "Véhicules", value: data.nb_vehicles, icon: Car },
  ];

  return (
    <div>
      <PageHeader title="Statistiques" subtitle="Vue analytique de votre activité" icon={BarChart3} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{c.label}</span>
              <c.icon className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Évolution du chiffre d'affaires</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart} margin={{ left: -20, right: 5, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8 }} formatter={(v) => [eur(v), "CA"]} cursor={{ fill: "rgba(16,185,129,0.08)" }} />
                <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Prestations les plus réalisées</h3>
          {data.top_services?.length ? (
            <div className="space-y-3">
              {data.top_services.map((s, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-700">{s.name}</span><span className="font-mono text-slate-500">{s.count}</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(s.count / data.top_services[0].count) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500">Pas encore de données. Associez des prestations à vos rendez-vous.</p>}
          <div className="mt-6 pt-4 border-t border-slate-200 text-sm text-slate-500">Dépenses totales : <span className="font-mono text-red-600">{eur(data.depenses)}</span></div>
        </div>
      </div>
    </div>
  );
}
